// src/pages/SandaTourPage.js - 画像プリロード統合版
import React, { useState, useEffect } from 'react';
import { sandaTourData, supportedLanguages, languageFlags, COMMON_IMAGES } from '../data/sandaTourData';


// 画像リスト定義（コンポーネント内）
const SANDA_TOUR_IMAGES = [
  // ヒーロー画像
  COMMON_IMAGES.hero,
  
  // 体験画像
  COMMON_IMAGES.experiences.pottery,
  COMMON_IMAGES.experiences.cuisine,
  COMMON_IMAGES.experiences.tea,
  
  // ギャラリー画像
  ...COMMON_IMAGES.gallery
].filter(Boolean);

// 画像プリロード関数（コンポーネント内）
const preloadImages = (imageUrls, onProgress, onComplete) => {
  const totalImages = imageUrls.length;
  let loadedImages = 0;

  if (totalImages === 0) {
    onComplete();
    return Promise.resolve([]);
  }

  const imagePromises = imageUrls.map((url) => {
    return new Promise((resolve) => {
      const img = new Image();
      
      const handleComplete = () => {
        loadedImages++;
        const progress = Math.round((loadedImages / totalImages) * 100);
        onProgress(progress, loadedImages, totalImages);
        
        if (loadedImages === totalImages) {
          setTimeout(() => onComplete(), 300);
        }
        resolve();
      };
      
      img.onload = handleComplete;
      img.onerror = handleComplete; // エラーでも続行
      
      // タイムアウト設定（10秒）
      setTimeout(() => {
        if (!img.complete) {
          handleComplete();
        }
      }, 10000);
      
      img.src = url;
    });
  });

  return Promise.allSettled(imagePromises);
};

// ローディング画面コンポーネント（インライン）
const LoadingScreen = ({ progress = 0, isVisible = true }) => {
  const styles = {
    overlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      backgroundColor: '#1a252f',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 9999,
      opacity: isVisible ? 1 : 0,
      visibility: isVisible ? 'visible' : 'hidden',
      transition: 'opacity 0.8s ease-out, visibility 0.8s ease-out',
    },
    content: {
      textAlign: 'center',
      color: '#ffffff',
      maxWidth: '400px',
      padding: '0 20px',
    },
    logo: {
      width: '120px',
      height: 'auto',
      marginBottom: '40px',
      opacity: 0.9,
    },
    title: {
      fontSize: '24px',
      fontWeight: 'bold',
      marginBottom: '10px',
      fontFamily: "'Noto Serif JP', serif",
    },
    subtitle: {
      fontSize: '14px',
      color: '#9ca3af',
      marginBottom: '50px',
      lineHeight: '1.6',
    },
    progressContainer: {
      width: '100%',
      marginBottom: '20px',
    },
    progressBar: {
      width: '100%',
      height: '4px',
      backgroundColor: 'rgba(255, 255, 255, 0.1)',
      borderRadius: '2px',
      overflow: 'hidden',
      position: 'relative',
    },
    progressFill: {
      height: '100%',
      backgroundColor: '#4a9b8e',
      borderRadius: '2px',
      transition: 'width 0.3s ease-out',
      width: `${progress}%`,
      position: 'relative',
      overflow: 'hidden',
    },
    progressGlow: {
      position: 'absolute',
      top: 0,
      right: '-10px',
      width: '10px',
      height: '100%',
      background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent)',
      animation: progress < 100 ? 'shimmer 1.5s infinite' : 'none',
    },
    progressText: {
      fontSize: '12px',
      color: '#d4af37',
      fontWeight: 'bold',
      marginTop: '10px',
    },
    loadingDots: {
      display: 'flex',
      justifyContent: 'center',
      gap: '4px',
      marginTop: '30px',
    },
    dot: {
      width: '8px',
      height: '8px',
      backgroundColor: '#4a9b8e',
      borderRadius: '50%',
      animation: 'loadingDots 1.4s ease-in-out infinite both',
    },
  };

  return (
    <div style={styles.overlay}>
      <style>
        {`
          @keyframes shimmer {
            0% { transform: translateX(-100%); }
            100% { transform: translateX(300%); }
          }
          
          @keyframes loadingDots {
            0%, 80%, 100% {
              transform: scale(0.8);
              opacity: 0.6;
            }
            40% {
              transform: scale(1);
              opacity: 1;
            }
          }
          
          .loading-dot:nth-child(1) { animation-delay: -0.32s; }
          .loading-dot:nth-child(2) { animation-delay: -0.16s; }
          .loading-dot:nth-child(3) { animation-delay: 0s; }
        `}
      </style>
      
      <div style={styles.content}>
        <p style={styles.subtitle}>
          画像を読み込んでいます...<br />
          しばらくお待ちください
        </p>
        
        <div style={styles.progressContainer}>
          <div style={styles.progressBar}>
            <div style={styles.progressFill}>
              <div style={styles.progressGlow}></div>
            </div>
          </div>
          <div style={styles.progressText}>
            {progress}% 完了
          </div>
        </div>
        
        <div style={styles.loadingDots}>
          <div style={styles.dot} className="loading-dot"></div>
          <div style={styles.dot} className="loading-dot"></div>
          <div style={styles.dot} className="loading-dot"></div>
        </div>
      </div>
    </div>
  );
};

const SandaTourPage = () => {
  const [currentLang, setCurrentLang] = useState('ja');
  const [selectedGalleryImage, setSelectedGalleryImage] = useState(null);
  const [scrollY, setScrollY] = useState(0);
  const [visibleSections, setVisibleSections] = useState(new Set());
  
  
  // 画像プリロード関連のstate
  const [isLoading, setIsLoading] = useState(true);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [showContent, setShowContent] = useState(false);

  // 画像プリロード処理
  useEffect(() => {
    console.log('画像プリロード開始:', SANDA_TOUR_IMAGES.length, '枚');
    
    preloadImages(
      SANDA_TOUR_IMAGES,
      // 進捗コールバック
      (progress, loaded, total) => {
        setLoadingProgress(progress);
        console.log(`読み込み進捗: ${loaded}/${total} (${progress}%)`);
      },
      // 完了コールバック
      () => {
        console.log('全画像読み込み完了');
        setTimeout(() => {
          setIsLoading(false);
          setTimeout(() => {
            setShowContent(true);
          }, 500);
        }, 800);
      }
    );
  }, []);

  // スクロール位置を追跡
  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // URLクエリパラメータから言語を取得
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const lang = urlParams.get('lang');
    if (lang && supportedLanguages.includes(lang)) {
      setCurrentLang(lang);
    }
  }, []);

  // スクロールアニメーション
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleSections(prev => new Set([...prev, entry.target.id]));
          }
        });
      },
      {
        threshold: 0.1,
        rootMargin: '0px 0px -50px 0px'
      }
    );

    const sections = document.querySelectorAll('[data-animate]');
    sections.forEach(section => observer.observe(section));

    return () => observer.disconnect();
  }, [showContent]);

  // 言語切り替え
  const changeLanguage = (lang) => {
    setCurrentLang(lang);
    const url = new URL(window.location);
    url.searchParams.set('lang', lang);
    window.history.pushState({}, '', url);
  };

  // ギャラリー画像クリック
  const handleGalleryClick = (image) => {
    setSelectedGalleryImage(image);
  };

  // ライトボックス閉じる
  const closeLightbox = () => {
    setSelectedGalleryImage(null);
  };

  const data = sandaTourData[currentLang];

  // コンテンツスタイル（フェードイン制御）
  const contentStyles = {
    opacity: showContent ? 1 : 0,
    transition: 'opacity 1s ease-in-out',
    visibility: showContent ? 'visible' : 'hidden',
  };

  const styles = {
    page: {
      minHeight: '100vh',
      fontFamily: "'Noto Serif JP', serif",
      lineHeight: '1.8',
      color: '#2c3e50',
      overflow: 'hidden',
      ...contentStyles,
    },
    languageSelector: {
      position: 'fixed',
      top: '20px',
      right: '20px',
      zIndex: 1000,
      display: 'flex',
      gap: '8px',
      background: 'rgba(255, 255, 255, 0.95)',
      padding: '12px 16px',
      borderRadius: '25px',
      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
      backdropFilter: 'blur(10px)',
    },
    flagButton: {
      background: 'none',
      border: '2px solid transparent',
      cursor: 'pointer',
      padding: '4px',
      borderRadius: '6px',
      transition: 'all 0.2s',
      opacity: 0.7,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    flagButtonActive: {
      opacity: 1,
      borderColor: '#4a9b8e',
      background: 'rgba(74, 155, 142, 0.1)',
    },
    hero: {
      height: '100vh',
      position: 'relative',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: '#ffffff',
      textAlign: 'center',
      overflow: 'hidden',
    },
    heroBackground: {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '120%',
      backgroundImage: `url('${data.hero.imageUrl}')`,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      transform: `translateY(${scrollY * 0.5}px)`,
      willChange: 'transform',
    },
    heroOverlay: {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      background: 'linear-gradient(rgba(44, 62, 80, 0.3), rgba(44, 62, 80, 0.3))',
      zIndex: 1,
    },
    heroContent: {
      maxWidth: '900px',
      padding: '0 20px',
      position: 'relative',
      zIndex: 2,
      transform: `translateY(${scrollY * 0.2}px)`,
      willChange: 'transform',
    },
    heroTitle: {
      fontSize: '4rem',
      fontWeight: 'bold',
      marginBottom: '30px',
      textShadow: '2px 2px 4px rgba(0,0,0,0.7)',
      letterSpacing: '0.1em',
      lineHeight: '1.2',
      animation: showContent ? 'fadeInUp 1.5s ease-out' : 'none',
    },
    heroSubtitle: {
      fontSize: '1.6rem',
      marginBottom: '40px',
      textShadow: '1px 1px 2px rgba(0,0,0,0.7)',
      fontWeight: 300,
      lineHeight: '1.4',
      animation: showContent ? 'fadeInUp 1.8s ease-out' : 'none',
    },
    heroLocation: {
      fontSize: '1.3rem',
      marginBottom: '50px',
      color: '#4a9b8e',
      fontWeight: 'bold',
      background: 'rgba(255,255,255,0.95)',
      display: 'inline-block',
      padding: '12px 30px',
      borderRadius: '25px',
      animation: showContent ? 'fadeInUp 2.1s ease-out' : 'none',
    },
    heroCatchphrase: {
      fontSize: '2rem',
      fontStyle: 'italic',
      color: '#d4af37',
      textShadow: '1px 1px 2px rgba(0,0,0,0.7)',
      marginTop: '40px',
      fontWeight: 300,
      animation: showContent ? 'fadeInUp 2.4s ease-out' : 'none',
    },
    parallaxSection: {
      position: 'relative',
      overflow: 'hidden',
    },
    parallaxBackground: {
      position: 'absolute',
      top: '-20%',
      left: 0,
      width: '100%',
      height: '120%',
      backgroundAttachment: 'fixed',
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      willChange: 'transform',
    },
    parallaxContent: {
      position: 'relative',
      zIndex: 2,
      background: 'rgba(255, 255, 255, 0.95)',
      backdropFilter: 'blur(10px)',
    },
    section: {
      padding: '100px 20px',
      position: 'relative',
    },
    container: {
      maxWidth: '1200px',
      margin: '0 auto',
      position: 'relative',
      zIndex: 10,
    },
    sectionTitle: {
      fontSize: '3rem',
      textAlign: 'center',
      marginBottom: '80px',
      color: '#2c5f3f',
      fontWeight: 'bold',
      position: 'relative',
    },
    conceptSection: {
      background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
      padding: '120px 20px',
      textAlign: 'center',
      position: 'relative',
      overflow: 'hidden',
    },
    conceptBackground: {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      backgroundImage: `url('https://images.unsplash.com/photo-1578662015659-8b2bc4a4b893?w=1200&q=80')`,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundAttachment: 'fixed',
      opacity: 0.1,
      transform: `translateY(${scrollY * 0.3}px)`,
      willChange: 'transform',
    },
    conceptTitle: {
      fontSize: '3rem',
      color: '#2c5f3f',
      marginBottom: '50px',
      fontWeight: 'bold',
      position: 'relative',
      zIndex: 2,
    },
    conceptText: {
      fontSize: '1.4rem',
      color: '#495057',
      lineHeight: '2',
      maxWidth: '800px',
      margin: '0 auto',
      marginBottom: '60px',
      position: 'relative',
      zIndex: 2,
      textAlign: 'center',
    },
    conceptGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
      gap: '40px',
      marginTop: '80px',
      position: 'relative',
      zIndex: 2,
    },
    conceptItem: {
      background: 'rgba(255, 255, 255, 0.95)',
      padding: '40px',
      borderRadius: '15px',
      boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
      border: '2px solid #e9ecef',
      transition: 'all 0.3s ease',
      backdropFilter: 'blur(10px)',
      opacity: 0,
      transform: 'translateY(50px)',
      willChange: 'transform, opacity',
    },
    conceptItemVisible: {
      animation: 'conceptSlideIn 0.8s ease-out forwards',
      animationDelay: 'var(--concept-delay)',
    },
    conceptIcon: {
      fontSize: '4rem',
      marginBottom: '20px',
      color: '#4a9b8e',
    },
    conceptItemTitle: {
      fontSize: '1.5rem',
      color: '#2c5f3f',
      fontWeight: 'bold',
      marginBottom: '15px',
    },
    conceptItemText: {
      fontSize: '1rem',
      color: '#6c757d',
      lineHeight: '1.8',
    },
    experienceSection: {
      background: 'transparent',
      padding: '100px 20px',
      position: 'relative',
      overflow: 'hidden',
      minHeight: '100vh',
    },
    experienceBackground: {
      position: 'absolute',
      top: -1000,
      left: 0,
      width: '100%',
      height: '100%',
      backgroundImage: `url('https://lxnkiipxzsdpwledlbkp.supabase.co/storage/v1/object/public/experience-images/9a3775b5-5d19-4c03-85ec-4da3bd00c03d/760af57c-38c5-4073-a036-f5218dafc846.jpeg')`,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundAttachment: 'fixed',
      backgroundRepeat: 'no-repeat',
      opacity: 0.3,
      transform: `translateY(${scrollY * 0.5}px)`,
      willChange: 'transform',
      zIndex: 0,
    },
    experienceGrid: {
      display: 'grid',
      gap: '60px',
      marginTop: '80px',
    },
    experienceItem: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: '50px',
      alignItems: 'center',
      marginBottom: '60px',
      opacity: 0,
      transform: 'translateX(-500px)',
      willChange: 'transform, opacity',
      background: 'rgba(255, 255, 255, 0.9)',
      backdropFilter: 'blur(10px)',
      borderRadius: '20px',
      padding: '40px',
      boxShadow: '0 15px 35px rgba(0,0,0,0.1)',
      border: '1px solid rgba(255, 255, 255, 0.2)',
      position: 'relative',
      zIndex: 10,
    },
    experienceItemReverse: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: '50px',
      alignItems: 'center',
      marginBottom: '60px',
      opacity: 0,
      transform: 'translateX(-500px)',
      willChange: 'transform, opacity',
      background: 'rgba(255, 255, 255, 0.9)',
      backdropFilter: 'blur(10px)',
      borderRadius: '20px',
      padding: '40px',
      boxShadow: '0 15px 35px rgba(0,0,0,0.1)',
      border: '1px solid rgba(255, 255, 255, 0.2)',
      position: 'relative',
      zIndex: 10,
    },
    experienceItemVisible: {
    animation: 'slideInFromLeft 1s ease-out forwards',
    animationDelay: 'var(--delay)',
    },
    experienceItemReverseVisible: {
    animation:'slideInFromRight 1s ease-out forwards',
    animationDelay: 'var(--delay)',
    },
    experienceContent: {
      padding: '20px',
    },
    experienceTitle: {
      fontSize: '2.5rem',
      color: '#2c5f3f',
      fontWeight: 'bold',
      marginBottom: '15px',
      alignItems: 'center',
    },
    experienceIcon: {
      fontSize: '3rem',
    },
    experienceSubtitle: {
      fontSize: '1.3rem',
      color: '#4a9b8e',
      marginBottom: '20px',
      fontWeight: 'bold',
    },
    experienceTime: {
      fontSize: '1.1rem',
      color: '#d4af37',
      background: '#fff3cd',
      padding: '8px 20px',
      borderRadius: '20px',
      display: 'inline-block',
      marginBottom: '25px',
      fontWeight: 'bold',
    },
    experienceDescription: {
      fontSize: '1.1rem',
      color: '#495057',
      lineHeight: '1.8',
      marginBottom: '20px',
    },
    experienceDetails: {
      fontSize: '0.9rem',
      color: '#6c757d',
      fontStyle: 'italic',
    },
    experienceImage: {
      width: '100%',
      height: '400px',
      objectFit: 'cover',
      borderRadius: '15px',
      boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
      transition: 'transform 0.3s ease',
    },
    videoSection: {
      position: 'relative',
      padding: '100px 20px',
      textAlign: 'center',
      overflow: 'hidden',
    },
    videoBackground: {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      backgroundImage: `url('https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=1200&q=80')`,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundAttachment: 'fixed',
      transform: `translateY(${scrollY * 0.4}px)`,
      willChange: 'transform',
    },
    videoOverlay: {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      background: 'linear-gradient(135deg, rgba(74, 155, 142, 0.9), rgba(44, 95, 63, 0.9))',
      zIndex: 1,
    },
    videoContentWrapper: {
      position: 'relative',
      zIndex: 2,
    },
    videoTitle: {
      fontSize: '3rem',
      color: '#ffffff',
      marginBottom: '80px',
      fontWeight: 'bold',
    },
    videoContainer: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
      gap: '40px',
      marginTop: '60px',
    },
    videoItem: {
      background: 'rgba(255, 255, 255, 0.95)',
      borderRadius: '15px',
      overflow: 'hidden',
      boxShadow: '0 10px 30px rgba(0,0,0,0.2)',
      backdropFilter: 'blur(10px)',
    },
    videoFrame: {
      width: '100%',
      height: '250px',
      border: 'none',
      borderRadius: '15px 15px 0 0',
    },
    videoCaption: {
      padding: '20px',
      fontSize: '1.1rem',
      color: '#2c5f3f',
      fontWeight: 'bold',
    },
    gallerySection: {
      background: '#ffffff',
      padding: '100px 20px',
      position: 'relative',
    },
    galleryGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(4, 1fr)',
      gap: '20px',
      marginTop: '60px',
    },
    galleryItem: {
      position: 'relative',
      aspectRatio: '1',
      borderRadius: '15px',
      overflow: 'hidden',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      boxShadow: '0 5px 15px rgba(0,0,0,0.1)',
    },
    galleryImage: {
      width: '100%',
      height: '100%',
      objectFit: 'cover',
      transition: 'transform 0.3s ease',
    },
    galleryOverlay: {
      position: 'absolute',
      bottom: 0,
      left: 0,
      right: 0,
      background: 'linear-gradient(transparent, rgba(0,0,0,0.7))',
      color: '#ffffff',
      padding: '20px',
      transform: 'translateY(100%)',
      transition: 'transform 0.3s ease',
    },
    galleryCaption: {
      fontSize: '0.9rem',
      fontWeight: 'bold',
      textShadow: '1px 1px 2px rgba(0,0,0,0.8)',
    },
    infoSection: {
      position: 'relative',
      padding: '100px 20px',
      overflow: 'hidden',
    },
    infoBackground: {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      backgroundImage: `url('https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=1200&q=80')`,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundAttachment: 'fixed',
      transform: `translateY(${scrollY * 0.2}px)`,
      willChange: 'transform',
    },
    infoOverlay: {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      background: 'linear-gradient(135deg, rgba(44, 95, 63, 0.9), rgba(74, 155, 142, 0.9))',
      zIndex: 1,
    },
    infoContentWrapper: {
      position: 'relative',
      zIndex: 2,
    },
    infoSectionTitle: {
      fontSize: '3rem',
      textAlign: 'center',
      marginBottom: '80px',
      color: '#ffffff',
      fontWeight: 'bold',
    },
    infoGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
      gap: '50px',
      marginTop: '60px',
    },
    infoCard: {
      background: 'rgba(255, 255, 255, 0.15)',
      padding: '40px',
      borderRadius: '15px',
      backdropFilter: 'blur(15px)',
      border: '1px solid rgba(255, 255, 255, 0.2)',
    },
    infoCardTitle: {
      fontSize: '1.8rem',
      fontWeight: 'bold',
      marginBottom: '20px',
      color: '#ffffff',
    },
    infoTable: {
      width: '100%',
      marginTop: '20px',
    },
    infoTableRow: {
      borderBottom: '1px solid rgba(255, 255, 255, 0.2)',
    },
    infoTableHeader: {
      padding: '15px 10px',
      fontSize: '1rem',
      fontWeight: 'bold',
      color: '#d4af37',
      textAlign: 'left',
      minWidth: '120px',
    },
    infoTableData: {
      padding: '15px 10px',
      fontSize: '1rem',
      color: '#ffffff',
      lineHeight: '1.6',
    },
    contactSection: {
      background: '#2c3e50',
      color: '#ffffff',
      padding: '100px 20px',
      textAlign: 'center',
      position: 'relative',
    },
    contactTitle: {
      fontSize: '3rem',
      marginBottom: '60px',
      color: '#ffffff',
    },
    contactInfo: {
      maxWidth: '600px',
      margin: '0 auto',
    },
    contactItem: {
      fontSize: '1.3rem',
      marginBottom: '25px',
      lineHeight: '1.8',
    },
    reservationButton: {
      display: 'inline-block',
      backgroundColor: '#d4af37',
      color: '#2c3e50',
      padding: '20px 50px',
      fontSize: '1.4rem',
      fontWeight: 'bold',
      borderRadius: '30px',
      textDecoration: 'none',
      marginTop: '40px',
      transition: 'all 0.3s',
      boxShadow: '0 6px 20px rgba(212, 175, 55, 0.4)',
    },
    lightbox: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.9)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 2000,
      cursor: 'pointer',
    },
    lightboxImage: {
      maxWidth: '90%',
      maxHeight: '90%',
      objectFit: 'contain',
    },
    lightboxClose: {
      position: 'absolute',
      top: '20px',
      right: '20px',
      color: '#ffffff',
      fontSize: '2rem',
      cursor: 'pointer',
      background: 'rgba(0, 0, 0, 0.5)',
      borderRadius: '50%',
      width: '40px',
      height: '40px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    footer:{
      background: '#1a252f',
      color: '#ffffff',
    }
  };

  return (
    <>
      {/* ローディング画面 */}
      <LoadingScreen 
        progress={loadingProgress} 
        isVisible={isLoading} 
      />
      
      {/* メインコンテンツ */}
      <div style={styles.page}>
        <style>
          {`
            @import url('https://fonts.googleapis.com/css2?family=Noto+Serif+JP:wght@300;400;600;700&display=swap');
            
            @keyframes fadeInUp {
              from {
                opacity: 0;
                transform: translateY(30px);
              }
              to {
                opacity: 1;
                transform: translateY(0);
              }
            }
            
            @keyframes conceptSlideIn {
              from {
                opacity: 0.3;
                transform: translateY(50px);
              }
              to {
                opacity: 1;
                transform: translateY(0);
              }
            }
            
            @keyframes slideInFromLeft {
              from {
                opacity: 0;
                transform: translateX(-300px);
              }
              to {
                opacity: 1;
                transform: translateX(0);
              }
            }
            
            @keyframes slideInFromRight {
              from {
                opacity: 0;
                transform: translateX(300px);
              }
              to {
                opacity: 1;
                transform: translateX(0);
              }
            }
            
            @keyframes slideInUp {
              from {
                opacity: 0;
                transform: translateY(50px);
              }
              to {
                opacity: 1;
                transform: translateY(0);
              }
            }
            
            .concept-item:nth-child(1) { --concept-delay: 0.2s; }
            .concept-item:nth-child(2) { --concept-delay: 0.4s; }
            .concept-item:nth-child(3) { --concept-delay: 0.6s; }
            
            .experience-item:nth-child(1) { --delay: 0.2s; }
            .experience-item:nth-child(2) { --delay: 0.4s; }
            .experience-item:nth-child(3) { --delay: 0.6s; }
            
            @media (max-width: 768px) {
            
              .hero-title-mobile { font-size: 1.5rem !important; line-height: 1.3 !important; }
              .hero-subtitle-mobile { font-size: 0.9rem !important; }
              .hero-location-mobile { font-size: 0.9rem !important; }
              .hero-heroCatchphrase-mobile { font-size: 1.2rem !important; }
              .section-title-mobile { font-size: 1.8rem !important; }
              .concept-title-mobile { font-size: 1.5rem !important; }
              .concept-text-mobile { font-size: 1.1rem !important; }
              .concept-grid-mobile { 
                grid-template-columns: 1fr !important; 
                gap: 30px !important; 
              }
              .experience-item-mobile { 
                grid-template-columns: 1fr !important; 
                gap: 30px !important;
                padding: 10px !important;
                opacity: 1 !important;
                transform: translateX(0) !important;
                animation: none !important;                
              }
              .experience-item-reverse-mobile { 
                grid-template-columns: 1fr !important; 
                gap: 30px !important;
                padding: 10px !important;
                opacity: 1 !important;
                transform: translateX(0) !important;
                animation: none !important;
              }
    
          
              .experience-title-mobile { 
                font-size: 1.8rem !important; 
                flex-direction: column !important;
                gap: 10px !important;
              }
              .video-container-mobile { 
                grid-template-columns: 1fr !important; 
                gap: 30px !important; 
              }
              .gallery-grid-mobile { 
                grid-template-columns: repeat(2, 1fr) !important; 
                gap: 15px !important; 
              }
              .info-grid-mobile { 
                grid-template-columns: 1fr !important; 
                gap: 30px !important; 
              }
              .language-selector-mobile {
                top: 10px !important;
                right: 10px !important;
                padding: 8px 12px !important;
              }
            }
            
            .concept-item:hover {
              transform: translateY(-10px);
              box-shadow: 0 20px 40px rgba(0,0,0,0.15);
              border-color: #4a9b8e;
            }
            
            .experience-image:hover {
              transform: scale(1.05);
            }
            
            .video-item:hover {
              transform: translateY(-5px);
              box-shadow: 0 15px 35px rgba(0,0,0,0.2);
            }
            
            .gallery-item:hover .gallery-image {
              transform: scale(1.1);
            }
            
            .gallery-item:hover .gallery-overlay {
              transform: translateY(0);
            }
            
            .gallery-item:hover {
              box-shadow: 0 10px 30px rgba(0,0,0,0.2);
            }
            
            .reservation-button:hover {
              background-color: #f0c75e;
              transform: translateY(-3px);
              box-shadow: 0 8px 25px rgba(212, 175, 55, 0.6);
            }
            .contact-mobile h2{
              font-size: 1.5rem !important;}
              .contact-mobile div{
              font-size: 1rem !important;}

          
          `}
        </style>

        {/* 言語セレクター */}
        <div style={styles.languageSelector} className="language-selector-mobile">
          {supportedLanguages.map(lang => (
            <button
              key={lang}
              onClick={() => changeLanguage(lang)}
              style={{
                ...styles.flagButton,
                ...(currentLang === lang ? styles.flagButtonActive : {}),
              }}
              title={languageFlags[lang].name}
            >
              {languageFlags[lang].svg}
            </button>
          ))}
        </div>

        {/* ヒーローセクション（パララックス） */}
        <section style={styles.hero}>
          <div style={styles.heroBackground}></div>
          <div style={styles.heroOverlay}></div>
          <div style={styles.heroContent}>
            <h1 style={styles.heroTitle} className="hero-title-mobile">
              {data.hero.mainTitle}
            </h1>
            <p style={styles.heroSubtitle} className="hero-subtitle-mobile">
              {data.hero.subtitle}
            </p>
            <div style={styles.heroLocation} className="hero-location-mobile">
              {data.hero.location}
            </div>
            {data.hero.catchphrase && (
              <div style={styles.heroCatchphrase} className="hero-heroCatchphrase-mobile">
                {data.hero.catchphrase} 
              </div>
            )}
          </div>
        </section>

        {/* コンセプトセクション（パララックス背景） */}
        <section style={styles.conceptSection}>
          <div style={styles.conceptBackground}></div>
          <div style={styles.container}>
            <h2 style={styles.conceptTitle} className="concept-title-mobile">
              {currentLang === 'ja' && '日本文化の真髄を継承する'}
              {currentLang === 'en' && 'Inheriting the Essence of Japanese Culture'}
              {currentLang === 'zh' && '传承日本文化的精髓'}
              {currentLang === 'tw' && '傳承日本文化的精髓'}
              {currentLang === 'ko' && '일본 문화의 진수를 계승하다'}
            </h2>
            <p style={styles.conceptText} className="concept-text-mobile">
              {currentLang === 'ja' && '兵庫県三田市の伝統工芸「三田青磁」、そして三田の地と茶道裏千家との深いつながりを活かし、陶芸・茶道・和食という日本文化の粋を融合させた日帰りツアーです。本物志向の旅行者に向けて、五感で味わう日本文化の真髄をご体験ください。'}
              {currentLang === 'en' && 'This one-day tour combines the traditional craft of Sanda Celadon from Sanda City, Hyogo Prefecture, with the deep cultural connection between Sanda and the Urasenke school of tea ceremony. Blending pottery, tea ceremony, and Japanese cuisine—the essence of Japan\'s cultural heritage—it offers an immersive experience for travelers seeking authenticity and refinement.'}
              {currentLang === 'zh' && '兵库县三田市的传统工艺"三田青瓷"，以及三田与茶道裏千家之间的深厚渊源为基础，推出将陶艺、茶道与和食等日本文化精髓融为一体的一日游。。此行程面向追求真实体验的旅行者，邀请您通过五感品味日本文化的精髓。'}
              {currentLang === 'tw' && '以兵庫縣三田市的傳統工藝「三田青瓷」以及三田與茶道裏千家之間深厚的淵源為基礎，打造出結合陶藝、茶道與和食等日本文化精髓的一日游。此行程面向追求真實體驗的旅客，邀請您以五感品味日本文化的真髓。'}
              {currentLang === 'ko' && '효고현 산다시의 전통 공예인 산다 청자와, 산다 지역과 다도 우라센케의 깊은 인연을 바탕으로 도예·다도·일식 등 일본 문화의 정수를 결합한 당일치기 투어입니다. 진정한 체험을 추구하는 여행자를 위해, 오감을 통해 일본 문화의 진수를 느껴보시기 바랍니다。'}
            </p>
          </div>
        </section>

        {/* 体験内容セクション */}
        <section style={styles.experienceSection}>
          <div style={styles.experienceBackground}></div>
          <div style={styles.container}>
            <h2 style={styles.sectionTitle} className="section-title-mobile">
              {data.navigation.experience}
            </h2>
            
            {data.experiences.map((experience, index) => (
              <div 
                key={experience.id} 
                id={`experience-${index}`}
                data-animate
                style={{
                  ...(index % 2 === 0 ? styles.experienceItem : styles.experienceItemReverse),
                  ...(visibleSections.has(`experience-${index}`) 
                    ? (index % 2 === 0 ? styles.experienceItemVisible : styles.experienceItemReverseVisible)
                    : {}
                  ),
                  '--delay': `${0.2 + index * 0.2}s`
                }}
                className={`${index % 2 === 0 ? 'experience-item' : 'experience-item-reverse'} ${index % 2 === 0 ? "experience-item-mobile" : "experience-item-reverse-mobile"}`}
              >
                {index % 2 === 0 ? (
                  <>
                    <div style={styles.experienceContent}>
                      <h3 style={styles.experienceTitle} className="experience-title-mobile">
                        <span style={styles.experienceIcon}>{experience.icon}</span>
                        {experience.title}
                      </h3>
                      <h4 style={styles.experienceSubtitle}>{experience.subtitle}</h4>
                      <div style={styles.experienceTime}>{experience.time}</div>
                      <p style={styles.experienceDescription}>
                        {experience.description}
                      </p>
                      {experience.details.length > 0 && (
                        <div style={styles.experienceDetails}>
                          {experience.details.map((detail, idx) => (
                            <div key={idx}>{detail}</div>
                          ))}
                        </div>
                      )}
                    </div>
                    <img 
                      src={experience.imageUrl} 
                      alt={experience.title}
                      style={styles.experienceImage}
                      className="experience-image"
                    />
                  </>
                ) : (
                  <>
                    <div style={styles.experienceContent}>
                      <h3 style={styles.experienceTitle} className="experience-title-mobile">
                        <span style={styles.experienceIcon}>{experience.icon}</span>
                        {experience.title}
                      </h3>
                      <h4 style={styles.experienceSubtitle}>{experience.subtitle}</h4>
                      <div style={styles.experienceTime}>{experience.time}</div>
                      <p style={styles.experienceDescription}>
                        {experience.description}
                      </p>
                      {experience.details.length > 0 && (
                        <div style={styles.experienceDetails}>
                          {experience.details.map((detail, idx) => (
                            <div key={idx}>{detail}</div>
                          ))}
                        </div>
                      )}
                    </div>
                    <img 
                      src={experience.imageUrl} 
                      alt={experience.title}
                      style={styles.experienceImage}
                      className="experience-image"
                    />
                  </>
                )}
              </div>
            ))}
          </div>
        </section>

        {/* 動画セクション（パララックス背景） */}
        <section style={styles.videoSection}>
          <div style={styles.videoBackground}></div>
          <div style={styles.videoOverlay}></div>
          <div style={styles.videoContentWrapper}>
            <div style={styles.container}>
              <h2 style={styles.videoTitle} className="section-title-mobile">
                {currentLang === 'ja' && '三田陶芸の森。について'}
                {currentLang === 'en' && 'About Sanda Tougei no Mori'}
                {currentLang === 'zh' && '关于三田陶艺之森。'}
                {currentLang === 'tw' && '關於三田陶藝之森。'}
                {currentLang === 'ko' && '산다 도예의 숲에 대해'}
              </h2>
              
              <div style={styles.videoContainer} className="video-container-mobile">
                <div style={styles.videoItem} className="video-item">
                  <iframe 
                    style={styles.videoFrame}
                    src="https://www.youtube.com/embed/6Xs5Rwti9b0"
                    title="三田陶芸の森。PR動画"
                    allowFullScreen
                  ></iframe>
                  <div style={styles.videoCaption}>
                    {currentLang === 'ja' && '三田陶芸の森。PR動画'}
                    {currentLang === 'en' && '三田陶芸の森。PR動画'}
                    {currentLang === 'zh' && '三田陶芸の森。PR動画'}
                    {currentLang === 'tw' && '三田陶芸の森。PR動画'}
                    {currentLang === 'ko' && '三田陶芸の森。PR動画'}
                  </div>
                </div>
                
                <div style={styles.videoItem} className="video-item">
                  <iframe 
                    style={styles.videoFrame}
                    src="https://www.youtube.com/embed/e0t1aW2gBBg"
                    title="三田青磁PR動画"
                    allowFullScreen
                  ></iframe>
                  <div style={styles.videoCaption}>
                    {currentLang === 'ja' && '三田青磁PR動画'}
                    {currentLang === 'en' && '三田青磁PR動画'}
                    {currentLang === 'zh' && '三田青磁PR動画'}
                    {currentLang === 'tw' && '三田青磁PR動画'}
                    {currentLang === 'ko' && '三田青磁PR動画'}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ギャラリーセクション */}
        <section style={styles.gallerySection}>
          <div style={styles.container}>
            <h2 style={styles.sectionTitle} className="section-title-mobile">
              {data.gallery.title}
            </h2>
            <p style={styles.conceptText} className="concept-text-mobile">
              {data.gallery.subtitle}
            </p>
            
            <div style={styles.galleryGrid} className="gallery-grid-mobile">
              {data.gallery.images.map((image, index) => (
                <div 
                  key={image.id} 
                  style={styles.galleryItem}
                  className="gallery-item"
                  onClick={() => handleGalleryClick(image)}
                >
                  <img 
                    src={image.url} 
                    alt={image.caption}
                    style={styles.galleryImage}
                    className="gallery-image"
                  />
                  <div style={styles.galleryOverlay} className="gallery-overlay">
                    <div style={styles.galleryCaption}>{image.caption}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* お問い合わせセクション */}
        <section style={styles.contactSection}>
          <div style={styles.container} className='contact-mobile'>
            <h2 style={styles.contactTitle}>{data.contact.title}</h2>
            <div style={styles.contactInfo}>
              <div style={styles.contactItem}>{data.contact.company}</div>
              <div style={styles.contactItem}>{data.contact.address}</div>
              <div style={styles.contactItem}>{data.contact.phone}</div>
              
              <a 
                href={data.contact.reservationLink}
                target="_blank"
                rel="noopener noreferrer"
                style={styles.reservationButton}
                className="reservation-button"
              >
                {data.contact.qrText}
              </a>
            </div>
          </div>
        </section>

        <footer style={styles.footer}>
          &copy; {new Date().getFullYear()} Sanda Tougei no Mori. All rights reserved.
        </footer>     

        {/* ライトボックス */}
        {selectedGalleryImage && (
          <div style={styles.lightbox} onClick={closeLightbox}>
            <div style={styles.lightboxClose} onClick={closeLightbox}>×</div>
            <img 
              src={selectedGalleryImage.url} 
              alt={selectedGalleryImage.caption}
              style={styles.lightboxImage}
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        )}
      </div>
    </>
  );
};

export default SandaTourPage;